// src/pages/modules/TicketEscalationReportPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTicketEscalationReport,
  exportTicketEscalationReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';
import { fetchDepartments } from 'store/slices/departmentSlice';
import { fetchInventoryCategories, fetchInventorySubcategories } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { AlertTriangle, Ticket, Clock, User, Filter, X, ChevronDown, ChevronUp, Building2, Hash } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';

const TicketEscalationReportPage = () => {
  const dispatch = useDispatch();

  const {
    ticketEscalationReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = ticketEscalationReport.data.summary || {};
  const results = ticketEscalationReport.data.results || [];
  const pagination = ticketEscalationReport.pagination || {};



  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    priority: 'all',
    assigned_to: '',
    created_by_department: 'all',
    assigned_to_department: 'all',
    sub_category: '',
    category: '',
    is_overdue: 'all',
  });

  // Get data from Redux state
  const employees = useSelector((state) => state.common?.employees?.items || []);
  const employeesLoading = useSelector((state) => state.common?.employees?.loading || false);
  const departments = useSelector((state) => state.department?.departments || []);
  const departmentsLoading = useSelector((state) => state.department?.isLoading || false);
  const inventoryCategories = useSelector((state) => state.common?.inventoryCategories?.items || []);
  const categoriesLoading = useSelector((state) => state.common?.inventoryCategories?.loading || false);
  const selectedCategory = useSelector((state) => state.common?.inventorySubcategories?.byCategory || {});
  const subcategoriesLoading = useSelector((state) => {
    const categoryId = advancedFilters.sub_category;
    return state.common?.inventorySubcategories?.byCategory?.[categoryId]?.loading || false;
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);


  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'escalation',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.priority !== 'all' && { priority: advancedFilters.priority }),
      ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
      ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
      ...(advancedFilters.assigned_to_department && { assigned_to_department: advancedFilters.assigned_to_department }),
      ...(advancedFilters.sub_category && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.is_overdue !== 'all' && { is_overdue: advancedFilters.is_overdue }),
    };


    dispatch(fetchTicketEscalationReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all data on component mount
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
    dispatch(fetchDepartments({ page: 1, pageSize: 1000, is_active: true }));
    dispatch(fetchInventoryCategories());
  }, [dispatch]);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (advancedFilters.category && advancedFilters.category !== 'all') {
      dispatch(fetchInventorySubcategories(advancedFilters.category));
    }
  }, [dispatch, advancedFilters.category]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset subcategory when category changes
      ...(key === 'category' && { sub_category: 'all' })
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      priority: 'all',
      assigned_to: '',
      created_by_department: 'all',
      assigned_to_department: 'all',
      sub_category: '',
      category: '',
      is_overdue: 'all',
    });
  };

  const columns = [
    {
      accessorKey: 'ticket_code',
      header: 'Ticket Code',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono">
          {row.original.ticket_code}
        </Badge>
      )
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="max-w-xs truncate" title={row.original.title}>
          {row.original.title}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        let variant = 'secondary';
        if (status === 'open') variant = 'default';
        if (status === 'in_progress') variant = 'default';
        if (status === 'closed') variant = 'outline';
        if (status === 'escalated') variant = 'destructive';
        return (
          <Badge variant={variant}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => {
        const priority = row.original.priority;
        let variant = 'secondary';
        if (priority === 'high') variant = 'destructive';
        if (priority === 'medium') variant = 'default';
        if (priority === 'low') variant = 'outline';
        return (
          <Badge variant={variant}>
            {priority}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.created_by_name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assigned_to_name',
      header: 'Assigned To',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.assigned_to_name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (
        formatDateTime(row.original.created_at) ?? '-'
      ),
    },
    {
      accessorKey: 'expected_solution_end_datetime',
      header: 'Expected Solution',
      cell: ({ row }) => {
        const date = row.original.expected_solution_end_datetime;
        const isOverdue = date && new Date(date) < new Date();
        return (
          <span className={`text-sm ${isOverdue ? 'text-destructive font-medium' : ''}`}>
            {date ? formatDateTime(row.original.expected_solution_end_datetime) : '-'}
          </span>
        );
      },
    },
  ];

  // Get subcategories for the selected category
  const subcategories = advancedFilters.category && advancedFilters.category !== 'all'
    ? selectedCategory[advancedFilters.category]?.items || []
    : [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Escalation Report"
        description="Overview of escalated tickets and compliance with escalation levels"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={advancedFilters.status}
                  onValueChange={(value) => handleAdvancedFilterChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="ack_pending">Awaiting Acknowledgement</SelectItem>

                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={advancedFilters.priority}
                  onValueChange={(value) => handleAdvancedFilterChange('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* <div className="space-y-2">
                <Label htmlFor="assigned_to">Assigned To</Label>
                <Select
                  value={advancedFilters.assigned_to}
                  onValueChange={(value) => handleAdvancedFilterChange('assigned_to', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                   
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}


              <FormField
                label="Assigned To"
                name="assigned_to"
                type="select"
                value={advancedFilters.assigned_to}
                onChange={(e) => handleAdvancedFilterChange('assigned_to', e.target.value)}
                disabled={employeesLoading}
                options={[
                  ...(Array.isArray(employees)
                    ? employees.map((employee) => ({
                      value: String(employee.id),
                      label: employee.name,
                    }))
                    : []),
                ]}
                placeholder={employeesLoading ? "Loading..." : "Select employee"}
              />


              {/* <div className="space-y-2">
                <Label htmlFor="created_by_department">Created By Department</Label>
                <Select
                  value={advancedFilters.created_by_department}
                  onValueChange={(value) => handleAdvancedFilterChange('created_by_department', value)}
                  disabled={departmentsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={departmentsLoading ? "Loading..." : "Select department"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(departments) && departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="assigned_to_department">Assigned To Department</Label>
                <Select
                  value={advancedFilters.assigned_to_department}
                  onValueChange={(value) => handleAdvancedFilterChange('assigned_to_department', value)}
                  disabled={departmentsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={departmentsLoading ? "Loading..." : "Select department"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(departments) && departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={advancedFilters.category}
                  onValueChange={(value) => handleAdvancedFilterChange('category', value)}
                  disabled={categoriesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(inventoryCategories) && inventoryCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category</Label>
                <Select
                  value={advancedFilters.sub_category}
                  onValueChange={(value) => handleAdvancedFilterChange('sub_category', value)}
                  disabled={subcategoriesLoading || !advancedFilters.category || advancedFilters.category === 'all'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      advancedFilters.category === 'all' 
                        ? "Select a category first" 
                        : subcategoriesLoading 
                          ? "Loading..." 
                          : "Select subcategory"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(subcategories) && subcategories.map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="is_overdue">Overdue Status</Label>
                <Select
                  value={advancedFilters.is_overdue}
                  onValueChange={(value) => handleAdvancedFilterChange('is_overdue', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select overdue status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Overdue</SelectItem>
                    <SelectItem value="false">Not Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Escalated</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_escalated || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Max Escalation Level</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.max_escalation_level || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Escalation Levels</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.average_escalation_levels || '0'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue Escalations</CardTitle>
            <Clock className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.overdue_escalations || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Escalation Rate</CardTitle>
            <AlertTriangle className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.escalation_rate || '0%'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading escalation data...</span>
        </div>
      ) : results.length === 0 ? (
        <Card className="py-10">
          <CardContent className="text-center text-muted-foreground">
            No escalated tickets found in the selected period.
          </CardContent>
        </Card>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by ticket code or title..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)} // Page reset is handled by useEffect
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportTicketEscalationReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'escalation',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
              ...(advancedFilters.priority !== 'all' && { priority: advancedFilters.priority }),
              ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
              ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
              ...(advancedFilters.assigned_to_department && { assigned_to_department: advancedFilters.assigned_to_department }),
              ...(advancedFilters.sub_category && { sub_category: advancedFilters.sub_category }),
              ...(advancedFilters.is_overdue !== 'all' && { is_overdue: advancedFilters.is_overdue }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default TicketEscalationReportPage;